import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { BASE_URL } from "../../commonUrls";
import { getPlannerStats } from "./TodayService";
import { getBudgets } from "../expense/transactions/BudgetService";
import { getPriorityProfile } from "../planner/PriorityService";
import "./LifePulseGauge.css";

// Deterministic, explainable — same "weighted factors + honest no-data stub"
// convention as the backend Priority Engine, just computed client-side from
// data four existing endpoints already return (no new backend work needed).
const BASE_WEIGHTS = { planner: 0.35, habits: 0.25, budget: 0.20, punctuality: 0.20 };

const ANALYTICS_URL = `${BASE_URL}/analytics/spending`;

async function fetchMonthSpend(from, to) {
  const res = await apiFetch(`${ANALYTICS_URL}?from=${from}&to=${to}&merchantLimit=1`);
  if (!res.ok) return null;
  return res.json();
}

function scorePlanner(stats) {
  const overdue = stats?.overdueCount ?? 0;
  let value, reason;
  if (overdue === 0) { value = 100; reason = "No overdue items"; }
  else if (overdue <= 2) { value = 70; reason = `${overdue} item${overdue > 1 ? "s" : ""} overdue`; }
  else if (overdue <= 5) { value = 45; reason = `${overdue} items overdue`; }
  else { value = 20; reason = `${overdue} items overdue — needs attention`; }
  return { key: "planner", label: "Planner", value, reason, dataAvailable: true };
}

function scoreHabits(habits) {
  const active = (habits || []).filter(h => h.active);
  if (active.length === 0) {
    return { key: "habits", label: "Habits", value: 60, reason: "No habits tracked yet", dataAvailable: false };
  }
  const daily = active.filter(h => h.frequency === "DAILY");
  if (daily.length === 0) {
    const anyStreak = active.some(h => (h.currentStreak || 0) > 0);
    return {
      key: "habits", label: "Habits",
      value: anyStreak ? 80 : 60,
      reason: anyStreak ? "Active streaks in progress" : "No habits due today",
      dataAvailable: true,
    };
  }
  const completedToday = daily.filter(h => h.completedToday).length;
  const value = Math.round((completedToday / daily.length) * 100);
  return {
    key: "habits", label: "Habits", value,
    reason: `${completedToday}/${daily.length} habits done today`,
    dataAvailable: true,
  };
}

function scoreBudget(budgets, analytics) {
  if (!Array.isArray(budgets) || budgets.length === 0) {
    return { key: "budget", label: "Budget", value: 60, reason: "No budgets set", dataAvailable: false };
  }
  const budgetByCategory = Object.fromEntries(budgets.map(b => [b.categoryId, Number(b.budgetAmount || 0)]));
  const spendByCategory = Object.fromEntries(
    (analytics?.categoryBreakdown || []).map(c => [c.categoryId, Number(c.amount || 0)])
  );
  let totalBudget = 0, totalSpend = 0;
  Object.entries(budgetByCategory).forEach(([catId, amt]) => {
    if (amt <= 0) return;
    totalBudget += amt;
    totalSpend += spendByCategory[catId] || 0;
  });
  if (totalBudget <= 0) {
    return { key: "budget", label: "Budget", value: 60, reason: "No budgets set", dataAvailable: false };
  }
  const pct = (totalSpend / totalBudget) * 100;
  let value, reason;
  if (pct < 70) { value = 100; reason = "Well within budget"; }
  else if (pct < 90) { value = 70; reason = "Approaching budget limits"; }
  else if (pct < 110) { value = 45; reason = "Near or over budget"; }
  else { value = 20; reason = "Over budget this month"; }
  return { key: "budget", label: "Budget", value, reason, dataAvailable: true };
}

function scorePunctuality(profile) {
  const p = profile?.overallPunctuality;
  if (p == null) {
    return { key: "punctuality", label: "Consistency", value: 60, reason: "Not enough completion history yet", dataAvailable: false };
  }
  const value = Math.round(p * 100);
  return {
    key: "punctuality", label: "Consistency", value,
    reason: value >= 70 ? "Finishing things on time" : "A few things slipping past their due date",
    dataAvailable: true,
  };
}

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
function lower(s) { return s ? s.charAt(0).toLowerCase() + s.slice(1) : s; }

function buildHeadline(factors) {
  const scored = factors.filter(f => f.dataAvailable);
  if (scored.length === 0) return "Just getting started — check back once there's more data.";
  const sorted = [...scored].sort((a, b) => a.value - b.value);
  const worst = sorted[0];
  const best = sorted[sorted.length - 1];
  const concern = worst.value < 70 ? worst.reason : null;
  const praise = best.value >= 80 && best.key !== worst.key ? best.reason : null;
  if (praise && concern) return `${capitalize(praise)} — but ${lower(concern)}.`;
  if (concern) return `${capitalize(concern)}.`;
  if (praise) return `${capitalize(praise)}.`;
  return "Steady day — nothing urgent, nothing to brag about either.";
}

export function computeLifePulse({ stats, habits, budgets, analytics, profile }) {
  const factors = [
    scorePlanner(stats),
    scoreHabits(habits),
    scoreBudget(budgets, analytics),
    scorePunctuality(profile),
  ];
  // Renormalize weights over whatever factors actually have real data — same
  // pattern PriorityWeightsProvider already uses — so a fresh account with no
  // habits/budgets yet isn't dragged down by neutral placeholder scores.
  const available = factors.filter(f => f.dataAvailable);
  const pool = available.length ? available : factors;
  const weightSum = pool.reduce((s, f) => s + (BASE_WEIGHTS[f.key] || 0), 0) || 1;
  const score = Math.round(
    pool.reduce((s, f) => s + (BASE_WEIGHTS[f.key] || 0) * f.value, 0) / weightSum
  );
  const band = score >= 75 ? "green" : score >= 45 ? "amber" : "red";
  return { score, band, factors, headline: buildHeadline(factors) };
}

export default function LifePulseGauge({ habits }) {
  const [stats, setStats] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    let alive = true;
    const now = new Date();
    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const todayStr = now.toISOString().slice(0, 10);
    Promise.all([
      getPlannerStats(),
      getBudgets(monthStr),
      fetchMonthSpend(`${monthStr}-01`, todayStr),
      getPriorityProfile().catch(() => null),
    ]).then(([s, b, a, p]) => {
      if (!alive) return;
      setStats(s);
      setBudgets(b || []);
      setAnalytics(a);
      setProfile(p);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const pulse = useMemo(
    () => computeLifePulse({ stats, habits, budgets, analytics, profile }),
    [stats, habits, budgets, analytics, profile]
  );

  if (loading) {
    return <div className="lp-card lp-loading">Calculating your Life Pulse…</div>;
  }

  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (pulse.score / 100) * circumference;

  return (
    <div className={`lp-card lp-${pulse.band}`}>
      <div className="lp-ring-wrap">
        <svg viewBox="0 0 100 100" className="lp-ring-svg">
          <circle cx="50" cy="50" r="42" className="lp-ring-track" />
          <circle
            cx="50" cy="50" r="42"
            className="lp-ring-fill"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="lp-ring-center">
          <div className="lp-ring-score">{pulse.score}</div>
          <div className="lp-ring-label">Life Pulse</div>
        </div>
      </div>

      <div className="lp-body">
        <div className="lp-headline">{pulse.headline}</div>
        <button className="lp-breakdown-toggle" onClick={() => setShowBreakdown(v => !v)}>
          {showBreakdown ? "Hide breakdown ▲" : "Show breakdown ▼"}
        </button>
        {showBreakdown && (
          <div className="lp-factors">
            {pulse.factors.map(f => (
              <div key={f.key} className={`lp-factor${f.dataAvailable ? "" : " lp-factor-stub"}`}>
                <div className="lp-factor-top">
                  <span className="lp-factor-label">{f.label}</span>
                  <span className="lp-factor-value">{f.dataAvailable ? f.value : "—"}</span>
                </div>
                <div className="lp-factor-bar-track">
                  <div className="lp-factor-bar-fill" style={{ width: `${f.dataAvailable ? f.value : 0}%` }} />
                </div>
                <div className="lp-factor-reason">
                  {f.reason}{!f.dataAvailable && <span className="lp-factor-nodata"> · no data yet</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
